import { useMemo, useState } from "react";
import { Donut } from "@/charts/Donut";
import { ActionCategory } from "@/generated/core/v1/actions";
import { MonthBars } from "@/charts/MonthBars";
import { formatMonth } from "@/plan-logic/lib/dates";
import { money } from "@/plan-logic/lib/format";
import { ChartInfo } from "@/components/ChartInfo";
import { Headline } from "@/components/Headline";
import { flatActions, monthlyFlows, spendingByCategory, whereNext } from "@/plan-logic/plan-view";
import { CATEGORY } from "../categories";
import { ConnectEmpty } from "../ConnectEmpty";
import { cashHeadCards } from "./cashHead";
import { PlanPage, type PlanCtx } from "../PlanPage";

type View = "pie" | "bars" | "flow" | "rec" | "next";
const VIEWS: [View, string][] = [
  ["pie", "Where your money goes"],
  ["bars", "Money in versus money out"],
  ["flow", "Which account paid what"],
  ["rec", "What comes every month"],
  ["next", "Where it should go next"],
];
const WINDOWS: [number, string][] = [
  [1, "The next month"],
  [3, "Next 3 months"],
  [6, "Next 6 months"],
  [12, "Next 12 months"],
];

function CashHeadline({ ctx }: { ctx: PlanCtx }) {
  const cards = useMemo(() => cashHeadCards(ctx.abi, ctx.naive), [ctx.abi, ctx.naive]);
  return <Headline id="hd-cf" cards={cards} />;
}

function Body({ ctx }: { ctx: PlanCtx }) {
  const { abi, index } = ctx;
  const [view, setView] = useState<View>("pie");
  const [months, setMonths] = useState<Record<string, number>>({ pie: 1, bars: 6 });
  const [open, setOpen] = useState<number | null>(null);
  const all = useMemo(() => flatActions(abi), [abi]);

  const win = months[view] ?? 3;
  const windowLabel = WINDOWS.find(([n]) => n === win)?.[1] ?? "";
  const dateBar = (
    <div className="cf-filters">
      {WINDOWS.map(([n, l]) => (
        <button
          type="button"
          key={n}
          className={`chip${win === n ? " on" : ""}`}
          onClick={() => setMonths((m) => ({ ...m, [view]: n }))}
        >
          {l}
        </button>
      ))}
    </div>
  );

  let title = windowLabel;
  let canvas;
  if (view === "pie") {
    const slices = spendingByCategory(abi, win, undefined, all)
      // Withdrawals are money coming back into chequing, not going out.
      .filter((s) => s.category !== ActionCategory.WITHDRAWAL)
      .map((s) => ({
        key: String(s.category),
        label: CATEGORY[s.category].label,
        value: s.monthly,
        color: CATEGORY[s.category].color,
      }));
    canvas = (
      <Donut
        slices={slices}
        label={`Where your money goes, ${windowLabel.toLowerCase()}, a month`}
      />
    );
  } else if (view === "bars") {
    canvas = <MonthBars rows={monthlyFlows(abi, win)} />;
  } else if (view === "flow") {
    title = "Which account paid what";
    canvas = <ConnectEmpty what="Which account paid for what comes from your transactions." />;
  } else if (view === "rec") {
    title = "What comes every month";
    canvas = (
      <ConnectEmpty what="Subscriptions and the bills that repeat every month come from your transactions." />
    );
  } else {
    title = "The next six months";
    const next = whereNext(abi, 6);
    const color = (c: number) => CATEGORY[c as keyof typeof CATEGORY]?.color ?? "var(--save)";
    canvas = (
      <>
        <p className="mut" style={{ margin: "0 0 16px", maxWidth: "72ch" }}>
          Once the essentials and the minimums are paid, this is where Abi would send what&rsquo;s
          left, month by month. The order is what makes it work: every dollar goes to the most
          expensive thing you owe until it&rsquo;s gone, then rolls into the next.
        </p>
        <div className="nextlist" data-testid="where-next">
          {next.map((n, i) => (
            <div className={`nextrow${open === i ? " on" : ""}`} key={i}>
              <button
                type="button"
                className="nexthead"
                onClick={() => setOpen(open === i ? null : i)}
                aria-expanded={open === i}
              >
                <span className="mo">{formatMonth(n.month)}</span>
                <span className="bar">
                  {n.items.map((t) => (
                    <span
                      key={t.targetUuid}
                      style={{ flex: t.amount, background: color(t.category) }}
                      title={`${index.name(t.targetUuid)}: ${money(t.amount)}`}
                    />
                  ))}
                </span>
                <span className="amt tnum">{money(n.total)}</span>
              </button>
              {open === i && (
                <div className="nextdet">
                  {n.items.length === 0 && <p className="mut">Nothing extra this month.</p>}
                  {n.items.map((t) => (
                    <div className="nd" key={t.targetUuid}>
                      <span className="sw" style={{ background: color(t.category) }} />
                      <span style={{ flex: 1 }}>
                        <b>{index.name(t.targetUuid)}</b>
                        <span className="wy">{CATEGORY[t.category].label}</span>
                      </span>
                      <span className="tnum">{money(t.amount)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </>
    );
  }

  return (
    <>
      <CashHeadline ctx={ctx} />

      <div className="cfvtabs" role="tablist" aria-label="Cash flow views">
        {VIEWS.map(([v, l]) => (
          <button
            type="button"
            key={v}
            role="tab"
            aria-selected={view === v}
            className={`cfvtab${view === v ? " on" : ""}`}
            onClick={() => setView(v)}
            data-testid={`cf-tab-${v}`}
          >
            {l}
          </button>
        ))}
      </div>

      <div className="card pad cfcard" id="cfcard" style={{ marginBottom: 16 }}>
        <div className="cf-bar">
          <h2 id="cf-win">
            {title}
            {/* beside the heading: the doughnut's legend fills the chart's top right corner */}
            <ChartInfo k="cf-canvas" inline />
          </h2>
          <div id="cf-dates">{view === "pie" || view === "bars" ? dateBar : null}</div>
        </div>
        <div id="cf-canvas" style={{ marginTop: 16 }}>
          {canvas}
        </div>
      </div>

      <div className="card pad" style={{ marginBottom: 16 }}>
        <h2 style={{ marginBottom: 10 }}>The transactions behind it</h2>
        <ConnectEmpty />
      </div>
    </>
  );
}

export function CashFlowPage() {
  return (
    <PlanPage title="pages.cashflow.title" sub="pages.cashflow.sub" testId="cashflow">
      {(ctx) => <Body ctx={ctx} />}
    </PlanPage>
  );
}
